import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Award, BookOpen, BookText, HelpCircle, FileText, Target, Database, Users, CreditCard, Megaphone, PanelLeft, ShieldCheck, Activity } from "lucide-react";
import logo from "@/assets/logo-light.png";
import { useAuth } from "@/contexts/AuthContext";
import { useSafeAreaTop } from "@/hooks/useSafeAreaTop";
import DemoModeToggle from "@/components/admin/DemoModeToggle";
import ScreenshotModeToggle from "@/components/admin/ScreenshotModeToggle";

const contentNavItems = [
  { title: "Certifications", url: "/admin/certifications", icon: Award },
  { title: "Chapters", url: "/admin/chapters", icon: BookOpen },
  { title: "Learning Objectives", url: "/admin/learning-objectives", icon: Target },
  { title: "Keywords", url: "/admin/keywords", icon: BookText },
  { title: "Questions", url: "/admin/questions", icon: HelpCircle },
  { title: "Exam Sets", url: "/admin/exam-sets", icon: FileText },
  { title: "Announcements", url: "/admin/announcements", icon: Megaphone },
  { title: "Pages", url: "/admin/pages", icon: PanelLeft },
];

// Users is available to all admins; the rest of management is super-admin only.
const managementNavItems = [
  { title: "Users", url: "/admin/users", icon: Users, superAdminOnly: false },
  { title: "Subscriptions", url: "/admin/subscriptions", icon: CreditCard, superAdminOnly: true },
  { title: "Data Management", url: "/admin/data-management", icon: Database, superAdminOnly: true },
  { title: "Quality", url: "/admin/quality", icon: ShieldCheck, superAdminOnly: true },
  { title: "Auth Events", url: "/admin/auth-events", icon: Activity, superAdminOnly: true },
];

const AdminSidebar = () => {
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  const safeTopFallback = useSafeAreaTop();
  const [newSignups, setNewSignups] = useState(0);

  // Count of registrations in the last 7 days, shown next to "Users".
  useEffect(() => {
    let cancelled = false;
    const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    supabase
      .from("profiles")
      .select("id", { count: "exact", head: true })
      .gte("created_at", since)
      .then(({ count }) => {
        if (!cancelled) setNewSignups(count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return (
    <aside className="w-60 h-full min-h-[100dvh] bg-sidebar text-sidebar-foreground flex flex-col shrink-0" style={{ paddingTop: `calc(env(safe-area-inset-top, 0px) + ${safeTopFallback}px)` }}>
      <NavLink to="/dashboard" className="block px-5 h-14 flex items-center border-b border-sidebar-border">
        <img src={logo} alt="A Software Tester" className="h-7 opacity-70 hover:opacity-100 transition-opacity" />
      </NavLink>

      <nav className="flex-1 min-h-0 py-4 px-3 space-y-1 overflow-y-auto scrollbar-ultra-thin">
        <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40">Content</p>
        {contentNavItems.map((item) => {
          const isActive = location.pathname.startsWith(item.url);
          return (
            <NavLink
              key={item.url}
              to={item.url}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-sidebar-primary text-sidebar-primary-foreground"
                  : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.title}
            </NavLink>
          );
        })}

        <div className="my-3 mx-3 border-t border-sidebar-border" />
        <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40">Management</p>
        {managementNavItems
          .filter((item) => !item.superAdminOnly || isSuperAdmin)
          .map((item) => {
            const isActive = location.pathname.startsWith(item.url);
            return (
              <NavLink
                key={item.url}
                to={item.url}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.title}
                {item.url === "/admin/users" && newSignups > 0 && (
                  <span
                    className="ml-auto rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-primary-foreground"
                    aria-label={`${newSignups} new registrations in the last 7 days`}
                    title="New registrations in the last 7 days"
                  >
                    {newSignups}
                  </span>
                )}
              </NavLink>
            );
          })}

        <div className="my-3 mx-3 border-t border-sidebar-border" />
        <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40">Tools</p>
        <div className="px-1 space-y-1.5">
          <ScreenshotModeToggle variant="inline" />
          <DemoModeToggle variant="inline" />
        </div>
      </nav>

    </aside>
  );
};

export default AdminSidebar;
